import { useState } from "react";
import { type CsvValue, downloadCsv } from "../csv.js";

export interface ActiveFilter {
  key: string;
  label: string;
  value: string;
}

export function ActiveFilters({
  filters,
  onClear,
  onRemove,
}: {
  filters: ActiveFilter[];
  onClear: () => void;
  onRemove: (key: string) => void;
}) {
  if (filters.length === 0) return null;
  return (
    <fieldset aria-label="Active filters" className="active-filters">
      <span className="label-13 dim">Active filters</span>
      {filters.map((filter) => (
        <button
          aria-label={`Remove ${filter.label} filter`}
          className="filter-chip"
          key={filter.key}
          onClick={() => onRemove(filter.key)}
          type="button"
        >
          <span>
            {filter.label}: {filter.value}
          </span>
          <span aria-hidden="true">×</span>
        </button>
      ))}
      <button className="filter-clear" onClick={onClear} type="button">
        Clear all
      </button>
    </fieldset>
  );
}

export function CopyButton({ label = "Copy", value }: { label?: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }
  return (
    <button className="copy-button" onClick={() => void copy()} type="button">
      {copied ? "Copied" : label}
    </button>
  );
}

export function CsvExportButton({
  filename,
  headers,
  rows,
}: {
  filename: string;
  headers: string[];
  rows: CsvValue[][];
}) {
  return (
    <button
      className="btn btn-quiet btn-sm"
      disabled={rows.length === 0}
      onClick={() => downloadCsv(filename, headers, rows)}
      type="button"
    >
      Export CSV
    </button>
  );
}

export function LoadingState({ label }: { label: string }) {
  return (
    <div className="loading-state" role="status">
      <span>{label}</span>
      <span className="loading-line" />
      <span className="loading-line loading-line-short" />
      <span className="loading-line" />
    </div>
  );
}

export function RetryBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="banner retry-banner" role="alert">
      <span>{message}</span>
      <button className="btn btn-quiet btn-sm" onClick={onRetry} type="button">
        Try again
      </button>
    </div>
  );
}

export function DatePresets({
  onSelect,
}: {
  onSelect: (range: { from: string; to: string }) => void;
}) {
  function select(days: number) {
    const to = new Date();
    const from = new Date(to);
    from.setUTCDate(from.getUTCDate() - (days - 1));
    const day = (value: Date) => value.toISOString().slice(0, 10);
    onSelect({ from: day(from), to: day(to) });
  }
  return (
    <fieldset aria-label="Date range shortcuts" className="date-presets">
      <button onClick={() => select(1)} type="button">
        Today
      </button>
      <button onClick={() => select(7)} type="button">
        7 days
      </button>
      <button onClick={() => select(30)} type="button">
        30 days
      </button>
    </fieldset>
  );
}

export function ResultCount({ count, noun }: { count: number; noun: string }) {
  const plural = noun === "entry" ? "entries" : `${noun}s`;
  return (
    <p className="result-count" role="status">
      Showing {count} {count === 1 ? noun : plural}
    </p>
  );
}

export function toIsoOrUndefined(value: string) {
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? undefined : instant.toISOString();
}

export function readUrlFilters<T extends object>(empty: T): T {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(Object.keys(empty).map((key) => [key, params.get(key) ?? ""])) as T;
}

export function writeUrlFilters(filters: object) {
  const url = new URL(window.location.href);
  for (const key of Object.keys(filters)) url.searchParams.delete(key);
  for (const [key, value] of Object.entries(filters)) if (value) url.searchParams.set(key, value);
  window.history.replaceState(null, "", url);
}

export function detailUrl(screen: string, id: string) {
  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set("screen", screen);
  url.searchParams.set("detail", id);
  return url.toString();
}

export function readDetailId() {
  return new URLSearchParams(window.location.search).get("detail");
}

export function writeDetailId(id: string | null) {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set("detail", id);
  else url.searchParams.delete("detail");
  window.history.replaceState(null, "", url);
}
